import { ContentSection } from "@components/LayoutProvider";
import useAnalytics from "@hooks/useAnalytics";
import useSession from "@hooks/useSession";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import React, { Children } from "react";
import { media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import useGigya from "../hooks/useGigya";

const Container = styled.nav`
  flex: 0 0 ${pxToRem(41)};
  display: flex;
  align-items: center;

  background: ${({ theme }) => `linear-gradient(308deg, ${theme.grey_600} 0%, black 345%)`};

  * {
    box-sizing: border-box; /* override NascarNav injected style */
  }
`;

const NavWrapper = styled.div`
  display: flex;
  text-transform: uppercase;
  white-space: nowrap;
  padding: 0 1rem;

  ${media.tablet} {
    justify-content: space-between;
  }

  ${media.mobile} {
    padding: 0 0.5rem;
  }

  button {
    padding: ${pxToRem(5)};
    background: none;
    border: none;
    color: white;
    text-transform: uppercase;
    text-decoration: none;
    font-size: ${pxToRem(12)};
    letter-spacing: 0px;
    transition: color 0.3s ease-out;

    &:focus,
    :focus-within {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.blueAlt};
    }

    &:not(:disabled) {
      &.active,
      :hover {
        color: ${({ theme }) => theme.gold};
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.18;
    }
  }
`;
const NascarLogo = styled.button`
  margin-right: ${pxToRem(58)};
  padding: ${pxToRem(4)} 0;
  outline-offset: ${pxToRem(-2)};

  ${media.tablet} {
    margin: 0;
    img {
      width: ${pxToRem(113)};
    }
  }
`;

const PrimaryNavContainer = styled.div`
  flex: 1 1 100%;
  display: flex;
  align-items: center;

  ${media.tablet} {
    display: none;
  }
`;

const SecondaryNavContainer = styled.div`
  flex: 0 1 auto;
  display: flex;
  align-items: center;

  a {
    ${media.tablet} {
      display: none;
    }
  }

  button {
    color: ${({ theme }) => theme.grey_300};

    &:not(:disabled) {
      &.active,
      :hover {
        color: ${({ theme }) => theme.gold};
      }
    }

    ${media.tablet} {
      color: white;
      &:not(:last-of-type) {
        display: none;
      }
    }
  }
`;

const MobileNavContainer = styled.div`
  display: none;

  ${media.tablet} {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    background-color: black;
    height: ${pxToRem(68)};

    button {
      width: ${pxToRem(50)};
      height: 100%;
      margin: 0;
      padding: 0 0 ${pxToRem(4)};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;

      background: none;
      border: none;
      border-bottom: ${pxToRem(3)} solid transparent;
      color: white;
      font-size: ${pxToRem(8)};
      font-weight: bold;
      text-transform: uppercase;
      transition: border-color 0.3s ease-out;

      &:focus,
      :focus-within {
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.blueAlt};
        outline-offset: ${pxToRem(-2)};
      }

      img {
        flex: 0 0 ${pxToRem(30)};
        width: ${pxToRem(36)};
        height: ${pxToRem(30)};
        margin: ${pxToRem(6)} 0 ${pxToRem(7)};
        padding: ${pxToRem(6)};
        object-fit: contain;
        border-radius: ${pxToRem(5)};
        background-color: ${({ theme }) => theme.grey_500};
        transition: background-color 0.2s ease-in-out;
      }

      &.active:not(:disabled) {
        border-color: ${({ theme }) => theme.goldAlt};

        img {
          background-color: ${({ theme }) => theme.grey_400};
        }
      }
    }
  }
`;

const GameNavbar = () => {
  const { showAuthModal, logout } = useGigya();
  const { trackEvent, currentPage } = useAnalytics();
  const { data: session } = useSession();
  const isLoggedIn = Boolean(session.user.token);

  const onLogoutClick = () => {
    trackEvent(`${currentPage}:logout`);
    logout();
  };

  return (
    <Container>
      <ContentSection>
        <NavWrapper>
          <Link href={"/"}>
            <NascarLogo>
              <img
                src={`${publicBasePath}/images/Playoffs-Grid-Challenge-Logo.png`}
                alt="NASCAR Playoffs Grid Challenge"
                width={151}
              />
            </NascarLogo>
          </Link>
          <PrimaryNavContainer>
            <NavLink href="/">
              <button>Your Picks</button>
            </NavLink>
            <NavLink href="/standings">
              <button>Overall Standings</button>
            </NavLink>
            <NavLink href="/leagues" activeClassName="active">
              <button>Leagues</button>
            </NavLink>
          </PrimaryNavContainer>
          <SecondaryNavContainer>
            <NavLink href="/prizes">
              <button>Prizes</button>
            </NavLink>
            <NavLink href="/how-to-play">
              <button>How To Play</button>
            </NavLink>
            <NavLink href="/rules">
              <button>Rules</button>
            </NavLink>
            {/* <a
              href="https://www.nascar.com/wp-content/uploads/sites/7/2023/08/28/2023-NASCAR-Cup-Series-Printable-Playoff-Grid-scaled.jpg"
              rel="noreferrer noopener"
              target="_blank"
            >
              <button onClick={() => trackEvent(`${currentPage}:print_grid`)}>
                Printable Grid
              </button>
            </a> */}
            {isLoggedIn ? (
              <button onClick={onLogoutClick}>Log Out</button>
            ) : (
              <button onClick={showAuthModal}>Log In</button>
            )}
          </SecondaryNavContainer>
        </NavWrapper>
        <MobileNavContainer>
          <NavLink href="/">
            <button>
              <img src={`${publicBasePath}/images/mobileNavIcons/helmet.png`} alt="Racing Helmet" />
              <span>Picks</span>
            </button>
          </NavLink>
          <NavLink href="/leagues">
            <button>
              <img src={`${publicBasePath}/images/mobileNavIcons/trophy.svg`} alt="League Trophy" />
              <span>Leagues</span>
            </button>
          </NavLink>
          <NavLink href="/standings">
            <button>
              <img src={`${publicBasePath}/images/mobileNavIcons/standings.svg`} alt="Bar Chart" />
              <span>Standings</span>
            </button>
          </NavLink>
          <NavLink href="/rules">
            <button>
              <img
                src={`${publicBasePath}/images/mobileNavIcons/rules.svg`}
                alt="Rules Clipboard"
              />
              <span>Rules</span>
            </button>
          </NavLink>
          <NavLink href="/how-to-play">
            <button>
              <img src={`${publicBasePath}/images/mobileNavIcons/how-to-play.svg`} alt="Notebook" />
              <span>
                How
                <br />
                To Play
              </span>
            </button>
          </NavLink>
          <NavLink href="/prizes">
            <button>
              <img src={`${publicBasePath}/images/mobileNavIcons/prizes.svg`} alt="Dollar Sign" />
              <span>Prizes</span>
            </button>
          </NavLink>
        </MobileNavContainer>
      </ContentSection>
    </Container>
  );
};

export default GameNavbar;

/**
 * Link component wrapper that enables the activeClassName prop.
 */
function NavLink({ children, activeClassName = "active", ...props }) {
  const { asPath } = useRouter();
  const child = Children.only(children);
  const childClassName = child?.props?.className || "";

  const className =
    asPath === props.href || asPath === props.as
      ? `${childClassName} ${activeClassName}`.trim()
      : childClassName;

  return (
    <Link {...(props as LinkProps)}>
      {React.cloneElement(child, {
        className: className || null,
      })}
    </Link>
  );
}
